<script setup lang="ts">
import { onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useUiStore } from '@/stores/ui'

/**
 * Velo di attesa per le operazioni bloccanti (il cambio lingua, che ricarica
 * tutte le stringhe, è la prima).
 *
 * Compare con un ritardo: quasi tutte le operazioni finiscono in poche decine di
 * millisecondi, e mostrarlo subito produrrebbe un lampo più fastidioso
 * dell'attesa che dovrebbe spiegare. Il ritardo usa `setTimeout` e non
 * `requestAnimationFrame` di proposito — vedi il commento nel template.
 */
const DELAY_MS = 120

const ui = useUiStore()
const { t } = useI18n()

const visible = ref(false)
let timer: ReturnType<typeof setTimeout> | null = null

function clear(): void {
  if (timer !== null) {
    clearTimeout(timer)
    timer = null
  }
}

watch(
  () => ui.isBusy,
  (busy) => {
    clear()

    if (!busy) {
      visible.value = false
      return
    }

    timer = setTimeout(() => {
      visible.value = true
      timer = null
    }, DELAY_MS)
  },
)

onUnmounted(clear)
</script>

<template>
  <!--
    Nessun <Transition>, e non è una scelta di stile: una transizione di ingresso
    che si blocca lascia l'elemento a `opacity: 0`, presente nel DOM ma invisibile.
    Le transizioni Vue avanzano con requestAnimationFrame, che viene congelato
    appena il documento è nascosto (basta una scheda in background): il velo
    resterebbe invisibile mentre l'app è bloccata, cioè il peggiore dei due esiti.
    Lo stato non animato è quindi quello visibile, e il cerchio che gira è
    un'animazione CSS che, se rallentata, lascia comunque tutto leggibile.
  -->
  <div
    v-if="visible"
    class="bg-abyss-900/50 fixed inset-0 z-90 flex items-center justify-center px-4"
    role="status"
    aria-live="polite"
  >
    <div class="panel flex items-center gap-3 px-5 py-4 shadow-raised">
      <span
        class="border-primary-200 border-t-primary-600 h-5 w-5 shrink-0 animate-spin rounded-full border-2 motion-reduce:animate-none"
        aria-hidden="true"
      />
      <p class="text-abyss-900 text-sm font-medium">{{ t('common.loading') }}</p>
    </div>
  </div>
</template>
